import { useEffect, useState } from 'react'
import { sileo } from 'sileo'
import { supabase } from '../lib/supabase'
import { useApp } from '../context/AppContext'
import notificationSubscriberService from '../services/notificationSubscriberService'
import smtpConfigService from '../services/smtpConfigService'
import usePushNotifications from '../hooks/usePushNotifications'
import SettingsSection from '../components/Settings/SettingsSection'
import '../styles/data-table.css'

const EMPTY_SMTP = {
  host: '',
  port: 587,
  username: '',
  password: '',
  fromEmail: '',
  fromName: '',
  secure: false,
  active: false,
}

/**
 * Ajustes de notificaciones de tickets:
 *  - Configuración del servidor SMTP para el envío de correos.
 *  - Lista de correos suscritos a las alertas (nuevos / sin asignar).
 *  - Activación de las notificaciones push de ESTE navegador.
 * @module NotificationSettings
 */
const NotificationSettings = () => {
  const [subscribers, setSubscribers] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ email: '', name: '' })

  // Config SMTP. `passwordSet` indica si ya hay una contraseña guardada (no se
  // muestra por seguridad); el campo de password solo se envía si el usuario
  // escribe uno nuevo.
  const [smtp, setSmtp] = useState(EMPTY_SMTP)
  const [passwordSet, setPasswordSet] = useState(false)
  const [savingSmtp, setSavingSmtp] = useState(false)
  const [testing, setTesting] = useState(false)

  const [testingPush, setTestingPush] = useState(false)

  const { user } = useApp()
  const push = usePushNotifications()

  // ==========================================
  // SMTP
  // ==========================================
  const loadSmtp = async () => {
    const res = await smtpConfigService.get()
    if (res.success && res.data) {
      const { password, ...rest } = res.data
      setPasswordSet(!!password)
      setSmtp({ ...EMPTY_SMTP, ...rest, password: '' })
    }
  }

  useEffect(() => {
    loadSmtp()
  }, [])

  const handleSaveSmtp = async (e) => {
    e.preventDefault()
    setSavingSmtp(true)
    // Solo enviamos password si el usuario escribió uno nuevo.
    const payload = {
      host: smtp.host?.trim() || null,
      port: Number(smtp.port) || 587,
      username: smtp.username?.trim() || null,
      fromEmail: smtp.fromEmail?.trim() || null,
      fromName: smtp.fromName?.trim() || null,
      secure: !!smtp.secure,
      active: !!smtp.active,
    }
    if (smtp.password) payload.password = smtp.password
    const res = await smtpConfigService.save(payload)
    setSavingSmtp(false)
    if (res.success) {
      if (smtp.password) setPasswordSet(true)
      setSmtp((s) => ({ ...s, password: '' }))
      sileo.success({ title: 'Configuración SMTP guardada' })
    } else {
      sileo.error({ title: 'No se pudo guardar', description: res.error })
    }
  }

  const handleTestSmtp = async () => {
    const to = user?.email
    if (!to) {
      sileo.error({
        title: 'Sin correo',
        description: 'Tu usuario no tiene correo.',
      })
      return
    }
    setTesting(true)
    try {
      const { data, error } = await supabase.functions.invoke(
        'ticket-notifications',
        {
          body: { event: 'test_email', to },
        }
      )
      if (error) throw error
      if (data?.ok) {
        sileo.success({
          title: 'Correo de prueba enviado',
          description: `Revisa ${to}`,
        })
      } else {
        sileo.error({
          title: 'No se pudo enviar',
          description: data?.email?.error || 'Revisa la configuración SMTP.',
        })
      }
    } catch (err) {
      sileo.error({ title: 'Error', description: err.message })
    } finally {
      setTesting(false)
    }
  }

  // ==========================================
  // SUSCRIPTORES
  // ==========================================
  const load = async () => {
    try {
      const res = await notificationSubscriberService.getAll()
      if (res.success) setSubscribers(res.data || [])
      else
        sileo.error({
          title: 'Error',
          description: 'No se pudieron cargar los suscriptores.',
        })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleAdd = async (e) => {
    e.preventDefault()
    const email = form.email.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      sileo.error({
        title: 'Correo inválido',
        description: 'Ingresa un correo válido.',
      })
      return
    }
    setSaving(true)
    const res = await notificationSubscriberService.create({
      email,
      name: form.name.trim() || null,
      onNewTicket: true,
      onUnassigned: true,
      active: true,
    })
    setSaving(false)
    if (res.success) {
      setForm({ email: '', name: '' })
      sileo.success({ title: 'Suscriptor agregado', description: email })
      load()
    } else {
      sileo.error({
        title: 'No se pudo agregar',
        description: res.error?.includes('duplicate')
          ? 'Ese correo ya está suscrito.'
          : res.error,
      })
    }
  }

  const toggleField = async (sub, field) => {
    const res = await notificationSubscriberService.update(sub.id, {
      [field]: !sub[field],
    })
    if (res.success) {
      setSubscribers((prev) =>
        prev.map((s) => (s.id === sub.id ? { ...s, [field]: !s[field] } : s))
      )
    } else {
      sileo.error({ title: 'Error', description: 'No se pudo actualizar.' })
    }
  }

  const handleRemove = async (sub) => {
    const res = await notificationSubscriberService.remove(sub.id)
    if (res.success) {
      setSubscribers((prev) => prev.filter((s) => s.id !== sub.id))
      sileo.success({ title: 'Suscriptor eliminado', description: sub.email })
    } else {
      sileo.error({ title: 'Error', description: 'No se pudo eliminar.' })
    }
  }

  // ==========================================
  // PUSH
  // ==========================================
  const handlePushToggle = async () => {
    if (push.isSubscribed) {
      await push.unsubscribe()
      sileo.info({ title: 'Notificaciones desactivadas' })
    } else {
      const ok = await push.subscribe()
      if (ok)
        sileo.success({ title: 'Notificaciones activadas en este navegador' })
      else if (push.error)
        sileo.error({ title: 'No se pudo activar', description: push.error })
    }
  }

  const handleTestPush = async () => {
    setTestingPush(true)
    try {
      const { data, error } = await supabase.functions.invoke(
        'ticket-notifications',
        {
          body: { event: 'test_push' },
        }
      )
      if (error) throw error
      if (data?.ok) {
        sileo.success({
          title: 'Notificación de prueba enviada',
          description: `Enviada a ${data.push} dispositivo(s). Revisa tu escritorio.`,
        })
      } else {
        sileo.error({
          title: 'No se pudo enviar',
          description:
            data?.error || 'Revisa la configuración de notificaciones.',
        })
      }
    } catch (err) {
      sileo.error({ title: 'Error', description: err.message })
    } finally {
      setTestingPush(false)
    }
  }

  return (
    <div className="ns">
      {/* ===== SERVIDOR SMTP ===== */}
      <SettingsSection
        title="Servidor de correo (SMTP)"
        icon="fa-server"
        description="Servidor corporativo usado para enviar las alertas por correo."
        actions={
          <>
            <span
              className={`tt-badge ns-status ${smtp.active ? 'is-on' : ''}`}
            >
              <i className="fa-solid fa-circle" />
              {smtp.active ? 'Activo' : 'Inactivo'}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={testing || savingSmtp}
              onClick={handleTestSmtp}
              title="Envía un correo de prueba a tu propia dirección"
            >
              {testing ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane me-1" />
                  Probar
                </>
              )}
            </button>
            <button
              type="submit"
              form="ns-smtp-form"
              className="btn btn-sm btn-primary"
              disabled={savingSmtp}
            >
              {savingSmtp ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <>
                  <i className="fa-solid fa-floppy-disk me-1" />
                  Guardar
                </>
              )}
            </button>
          </>
        }
      >
        <form id="ns-smtp-form" onSubmit={handleSaveSmtp}>
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <label className="ns-label" htmlFor="smtp-host">
                Host
              </label>
              <input
                id="smtp-host"
                type="text"
                className="form-control form-control-sm"
                placeholder="smtp.office365.com"
                value={smtp.host || ''}
                onChange={(e) =>
                  setSmtp((s) => ({ ...s, host: e.target.value }))
                }
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="ns-label" htmlFor="smtp-port">
                Puerto
              </label>
              <input
                id="smtp-port"
                type="number"
                className="form-control form-control-sm"
                placeholder="587"
                value={smtp.port ?? ''}
                onChange={(e) =>
                  setSmtp((s) => ({ ...s, port: e.target.value }))
                }
              />
            </div>
            <div className="col-6 col-md-3 d-flex align-items-end">
              <div
                className="form-check form-switch ns-switch"
                title="El puerto 587 usa STARTTLS. Activa TLS implícito solo si tu servidor usa el puerto 465."
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="smtp-secure"
                  checked={!!smtp.secure}
                  onChange={(e) =>
                    setSmtp((s) => ({ ...s, secure: e.target.checked }))
                  }
                />
                <label className="form-check-label" htmlFor="smtp-secure">
                  TLS implícito (465)
                </label>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <label className="ns-label" htmlFor="smtp-user">
                Usuario
              </label>
              <input
                id="smtp-user"
                type="text"
                className="form-control form-control-sm"
                placeholder="usuario@grupomecsa.net"
                autoComplete="off"
                value={smtp.username || ''}
                onChange={(e) =>
                  setSmtp((s) => ({ ...s, username: e.target.value }))
                }
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="ns-label" htmlFor="smtp-password">
                Contraseña
              </label>
              <input
                id="smtp-password"
                type="password"
                className="form-control form-control-sm"
                autoComplete="new-password"
                placeholder={
                  passwordSet ? '•••••••• (sin cambios)' : 'Contraseña'
                }
                value={smtp.password}
                onChange={(e) =>
                  setSmtp((s) => ({ ...s, password: e.target.value }))
                }
              />
            </div>

            <div className="col-12 col-md-6">
              <label className="ns-label" htmlFor="smtp-from">
                Correo remitente
              </label>
              <input
                id="smtp-from"
                type="email"
                className="form-control form-control-sm"
                placeholder="soporte@grupomecsa.net"
                value={smtp.fromEmail || ''}
                onChange={(e) =>
                  setSmtp((s) => ({ ...s, fromEmail: e.target.value }))
                }
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="ns-label" htmlFor="smtp-from-name">
                Nombre remitente (opcional)
              </label>
              <input
                id="smtp-from-name"
                type="text"
                className="form-control form-control-sm"
                placeholder="Mesa de Ayuda · Grupo Mecsa"
                value={smtp.fromName || ''}
                onChange={(e) =>
                  setSmtp((s) => ({ ...s, fromName: e.target.value }))
                }
              />
            </div>

            <div className="col-12">
              <div className="form-check form-switch ns-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="smtp-active"
                  checked={!!smtp.active}
                  onChange={(e) =>
                    setSmtp((s) => ({ ...s, active: e.target.checked }))
                  }
                />
                <label className="form-check-label" htmlFor="smtp-active">
                  Usar esta configuración para enviar correos
                </label>
              </div>
            </div>
          </div>
        </form>
      </SettingsSection>

      {/* ===== NOTIFICACIONES DEL NAVEGADOR ===== */}
      <SettingsSection
        title="Notificaciones del navegador"
        icon="fa-bell"
        description="Avisos del sistema en este dispositivo por tickets nuevos o sin asignar, aunque la pestaña esté cerrada."
        actions={
          <>
            {push.isSubscribed && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={testingPush || push.loading}
                onClick={handleTestPush}
                title="Envía una notificación de prueba a este navegador"
              >
                {testingPush ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <i className="fa-solid fa-paper-plane me-1" />
                    Probar
                  </>
                )}
              </button>
            )}
            <button
              type="button"
              className={`btn btn-sm ${push.isSubscribed ? 'btn-outline-danger' : 'btn-primary'}`}
              disabled={
                !push.supported || push.loading || push.permission === 'denied'
              }
              onClick={handlePushToggle}
            >
              {push.loading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : push.isSubscribed ? (
                <>
                  <i className="fa-solid fa-bell-slash me-1" />
                  Desactivar
                </>
              ) : (
                <>
                  <i className="fa-solid fa-bell me-1" />
                  Activar en este navegador
                </>
              )}
            </button>
          </>
        }
      >
        <div className="ns-note">
          {!push.supported ? (
            <>
              <i className="fa-solid fa-circle-info" />
              Este navegador no soporta notificaciones push.
            </>
          ) : push.permission === 'denied' ? (
            <span className="ns-danger">
              <i className="fa-solid fa-ban me-1" />
              Permiso bloqueado. Habilítalo en la configuración del navegador
              para este sitio.
            </span>
          ) : (
            <>
              <span
                className={`tt-badge ns-status ${push.isSubscribed ? 'is-on' : ''}`}
              >
                <i className="fa-solid fa-circle" />
                {push.isSubscribed ? 'Activadas' : 'Desactivadas'}
              </span>
              en este navegador
            </>
          )}
        </div>
      </SettingsSection>

      {/* ===== SUSCRIPTORES DE CORREO ===== */}
      <SettingsSection
        title="Correos suscritos"
        icon="fa-envelope"
        description="Reciben un aviso cuando entra un ticket nuevo o hay tickets sin asignar."
      >
        {/* Alta */}
        <form className="row g-2 align-items-end" onSubmit={handleAdd}>
          <div className="col-12 col-md-5">
            <label className="ns-label" htmlFor="sub-email">
              Correo
            </label>
            <input
              id="sub-email"
              type="email"
              className="form-control form-control-sm"
              placeholder="persona@grupomecsa.net"
              value={form.email}
              onChange={(e) =>
                setForm((f) => ({ ...f, email: e.target.value }))
              }
              required
            />
          </div>
          <div className="col-8 col-md-4">
            <label className="ns-label" htmlFor="sub-name">
              Nombre (opcional)
            </label>
            <input
              id="sub-name"
              type="text"
              className="form-control form-control-sm"
              placeholder="Nombre"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div className="col-4 col-md-3">
            <button className="btn btn-sm btn-primary w-100" disabled={saving}>
              {saving ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <>
                  <i className="fa-solid fa-plus me-1" />
                  Agregar
                </>
              )}
            </button>
          </div>
        </form>

        {/* Lista */}
        <div className="tt" style={{ flex: 'none' }}>
          <div className="tt-scroll">
            <table className="tt-table">
              <thead>
                <tr>
                  <th>Correo</th>
                  <th className="text-center">Nuevos</th>
                  <th className="text-center">Sin asignar</th>
                  <th className="text-center">Activo</th>
                  <th aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="tt-empty">
                      <span className="spinner-border spinner-border-sm text-primary" />
                    </td>
                  </tr>
                ) : subscribers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="tt-empty">
                      Aún no hay correos suscritos.
                    </td>
                  </tr>
                ) : (
                  subscribers.map((s) => (
                    <tr key={s.id} className={s.active ? '' : 'ns-inactive'}>
                      <td>
                        <span className="fw-semibold">{s.email}</span>
                        {s.name && (
                          <span className="tt-muted ms-2">{s.name}</span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="form-check form-switch ns-cell-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={!!s.onNewTicket}
                            onChange={() => toggleField(s, 'onNewTicket')}
                            aria-label={`Tickets nuevos para ${s.email}`}
                          />
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="form-check form-switch ns-cell-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={!!s.onUnassigned}
                            onChange={() => toggleField(s, 'onUnassigned')}
                            aria-label={`Tickets sin asignar para ${s.email}`}
                          />
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="form-check form-switch ns-cell-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={!!s.active}
                            onChange={() => toggleField(s, 'active')}
                            aria-label={`Suscripción activa para ${s.email}`}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="tt-actions">
                          <button
                            type="button"
                            className="tt-icon-btn is-danger"
                            title="Eliminar"
                            onClick={() => handleRemove(s)}
                          >
                            <i className="fa-solid fa-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </SettingsSection>

      <style>{`
        .ns-label {
          display: block; margin-bottom: 3px; font-size: 0.72rem; font-weight: 600;
          letter-spacing: 0.04em; text-transform: uppercase; color: var(--gm-text-muted);
        }
        .ns-switch { margin: 0 0 4px; min-height: 0; font-size: 0.8rem; color: var(--gm-text); }
        .ns-cell-switch { display: inline-flex; justify-content: center; margin: 0; padding-left: 2.5em; min-height: 0; }
        .ns-status { background: var(--gm-surface-2); color: var(--gm-text-muted); align-self: center; }
        .ns-status i { font-size: 0.45rem; }
        .ns-status.is-on { background: rgba(34, 197, 94, 0.14); color: var(--gm-green); }
        .ns-note { display: flex; align-items: center; gap: 8px; font-size: 0.8rem; color: var(--gm-text-muted); }
        .ns-danger { color: var(--gm-red); }
        .ns .tt-table tbody tr.ns-inactive td { opacity: 0.6; }
        .ns .tt-table tbody tr:hover .tt-actions { opacity: 1; }
      `}</style>
    </div>
  )
}

export default NotificationSettings
